import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import OpportunityCard from '@/components/OpportunityCard';
import SEOHead from '@/components/SEOHead';
import { ArrowRight, TrendingUp, Star, Briefcase, DollarSign, Clock, Sparkles, Car, Home, Laptop, GraduationCap, Package, Coins, ShoppingBag, Cpu, BarChart3, Megaphone, Globe, Gift, Wrench, MonitorSmartphone, Zap, Box, Shield, Eye, HeartPulse, Printer, MapPin, Gavel, Headphones, Receipt, Footprints, Gamepad2, Target, ChevronRight, CheckCircle, Users, Lock, Route, Clapperboard, Baby, PawPrint, HardHat, UtensilsCrossed, Camera, Stethoscope, MessageCircle, Scale, BrainCircuit, Landmark, Settings, Heart, HandHelping, Brush, BookOpen, Mic, Truck, ChefHat, Bike, Leaf, Activity, FlaskConical, CalendarDays, ShieldCheck, Smartphone, ClipboardList, Building, Compass, Hammer, FileText, Monitor, Wallet, MousePointer, Moon } from 'lucide-react';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useGlobalSettings } from '@/context/GlobalSettingsContext';
import { useAuth } from '@/context/AuthContext';
import { useTranslation } from '@/hooks/useTranslation';
import CategoryCard from '@/components/CategoryCard';
import HeroSearchBar from '@/components/HeroSearchBar';
import { QuestionnairePrompt } from '@/components/QuestionnairePrompt';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


function HomePage() {
  const navigate = useNavigate();
  const { currency, country: userCountry } = useGlobalSettings();
  const { isAuthenticated } = useAuth();
  const t = useTranslation();
  const [trendingOpportunities, setTrendingOpportunities] = useState([]);
  const [featuredOpportunities, setFeaturedOpportunities] = useState([]);
  const [stats, setStats] = useState({ total: 0 });
  const [trendingVisible, setTrendingVisible] = useState(6);
  const [matchScores, setMatchScores] = useState({});
  const [recommendations, setRecommendations] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [seoMeta, setSeoMeta] = useState(null);
  const [seoSchemas, setSeoSchemas] = useState([]);

  useEffect(() => {
    // Feb-2026 AI-cost: abort the /ai/recommendations call if the user
    // signs out / signs in mid-flight or unmounts. Cheap insurance against
    // StrictMode double-mount firing the LLM endpoint twice on dev.
    const ctrl = new AbortController();
    const init = async () => {
      try {
        const regionParam = userCountry || 'UK';
        const [trending, featured, all, metaRes, schemaRes] = await Promise.all([
          axios.get(`${API}/opportunities/trending?limit=24&region=${encodeURIComponent(regionParam)}`, { signal: ctrl.signal }),
          axios.get(`${API}/opportunities/featured?limit=6&region=${encodeURIComponent(regionParam)}`, { signal: ctrl.signal }),
          axios.get(`${API}/opportunities?limit=1`, { signal: ctrl.signal }),
          axios.get(`${API}/seo/metadata/home`, { signal: ctrl.signal }).catch(() => null),
          axios.get(`${API}/seo/schema/home`, { signal: ctrl.signal }).catch(() => null),
        ]);
        if (ctrl.signal.aborted) return;
        setTrendingOpportunities(trending.data);
        setFeaturedOpportunities(featured.data);
        setStats({ total: all.data.total || all.data.length || 0 });
        if (metaRes?.data) setSeoMeta(metaRes.data);
        if (schemaRes?.data?.schemas) setSeoSchemas(schemaRes.data.schemas);
      } catch {}

      try {
        const catRes = await axios.get(`${API}/categories/stats`, { signal: ctrl.signal });
        if (!ctrl.signal.aborted) setCategoryStats(catRes.data);
      } catch {}

      if (isAuthenticated && !ctrl.signal.aborted) {
        try {
          const rec = await axios.get(`${API}/ai/recommendations?limit=30`, { withCredentials: true, signal: ctrl.signal });
          if (ctrl.signal.aborted) return;
          if (rec.data?.length > 0) {
            const map = {};
            rec.data.forEach(o => { if (o.match_percentage) map[o.opportunity_id] = o.match_percentage; });
            setMatchScores(map);
            setRecommendations(rec.data.slice(0, 3));
          }
        } catch {}
      }
    };
    init();
    return () => { try { ctrl.abort(); } catch { /* noop */ } };
  }, [isAuthenticated]);

  const CATEGORY_ICONS = {
    'AI & Digital Work': Cpu, 'Freelancing': Laptop, 'Content Creation': Megaphone,
    'Vehicle-Based Income': Car, 'Property Rental': Home, 'Teaching': GraduationCap,
    'Local Services': Package, 'Passive Income': Coins, 'Reselling': ShoppingBag,
    'Investments': BarChart3, 'Gig Work': Wrench, 'Surveys & Rewards': Gift,
    'Online Marketplaces': Globe, 'Remote Work': MonitorSmartphone,
    'Micro-Earning Apps': Sparkles, 'Asset Rental': Box,
    'Expert Networks & Consulting': Briefcase, 'Specialized Resale': Gavel,
    'Field Audits & Intelligence': Eye, 'Software Testing & Cybersecurity': Shield,
    'Audio, Voice & Font Design': Headphones, 'Health & Research': HeartPulse,
    'Print-on-Demand': Printer, 'Travel & Local Guides': MapPin,
    'Cashback & Receipt Scanning': Receipt, 'Move-to-Earn': Footprints, 'Play-to-Earn': Gamepad2,
    'Modeling & Commercial Talent': Users, 'TV, Film & Extras': Clapperboard,
    'Kids & Family Modeling': Baby, 'Animal & Pet Talent': PawPrint,
    'Construction & Skilled Labor': HardHat, 'Healthcare Bank Work': HeartPulse,
    'Education & Supply Teaching': GraduationCap, 'Hospitality & Event Staffing': UtensilsCrossed,
    'Photography Gigs': Camera, 'Mystery Shopping & Audits': Eye,
    'Medical & HCP Panels': Stethoscope, 'Virtual Companionship': MessageCircle,
    'Legal & UX Testing': Scale,
    // New categories
    'AI Training & Data Annotation': BrainCircuit,
    'Asset Monetisation': Wallet,
    'Bookkeeping & Finance': Landmark,
    'Business Tools': Settings,
    'Care & Support Work': Heart,
    'Childcare & Domestic Work': HandHelping,
    'Cleaning & Maintenance': Brush,
    'Coaching & Course Marketplaces': BookOpen,
    'Community Healthcare': Activity,
    'Content Creation & Social Media': Mic,
    'Creator Economy': Zap,
    'Delivery & Logistics': Truck,
    'Food & Cooking': ChefHat,
    'Food Delivery': Bike,
    'Gaming & Esports': Gamepad2,
    'Green & Sustainability': Leaf,
    'Healthcare Flex Work': Activity,
    'Healthcare Gig Work': Stethoscope,
    'Healthcare Research': FlaskConical,
    'Healthcare Support Roles': Heart,
    'Hospitality & Events': CalendarDays,
    'Online Microtasks': ClipboardList,
    'Online Tutoring': Monitor,
    'Property & Space-Based Income': Building,
    'Research & Clinical Studies': FlaskConical,
    'Ride-Hailing': Car,
    'Seasonal & Outdoor Work': Leaf,
    'Seasonal Work': CalendarDays,
    'Security & Stewarding': ShieldCheck,
    'Shift-Based Work': Clock,
    'Shift-Based Work Apps': Smartphone,
    'Student & Campus Jobs': GraduationCap,
    'Student Jobs': GraduationCap,
    'Teaching & Tutoring': BookOpen,
    'Tourism & Experiences': Compass,
    'Trades & Local Skills': Hammer,
    'Transcription & Captioning': FileText,
    'User Testing & UX Research': MousePointer,
    'Virtual Assistance': MonitorSmartphone,
    'Weekend & Evening Jobs': Moon,
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <SEOHead metadata={seoMeta} schemas={seoSchemas} />
      <Header />

      {/* ═══ HERO — Search-First ═══ */}
      <section className="relative overflow-hidden" data-testid="hero-section" style={{ background: 'linear-gradient(180deg, #070D1B 0%, #0B1120 40%, #0D1526 100%)' }}>
        {/* Layer 1: Aurora background — smooth floating orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ transform: 'translate3d(0,0,0)', backfaceVisibility: 'hidden' }}>
          <div className="aurora-orb aurora-orb-1 absolute -top-24 left-[2%] w-[750px] h-[550px] rounded-full blur-[60px]" style={{ background: 'rgba(20, 184, 166, 0.28)' }} />
          <div className="aurora-orb aurora-orb-2 absolute -bottom-16 right-[2%] w-[650px] h-[500px] rounded-full blur-[70px]" style={{ background: 'rgba(59, 130, 246, 0.18)' }} />
          <div className="aurora-orb aurora-orb-3 absolute top-[8%] right-[15%] w-[550px] h-[550px] rounded-full blur-[75px]" style={{ background: 'rgba(56, 189, 248, 0.14)' }} />
          <div className="aurora-orb aurora-orb-4 absolute top-[55%] left-[30%] w-[500px] h-[400px] rounded-full blur-[60px]" style={{ background: 'rgba(139, 92, 246, 0.12)' }} />
        </div>

        {/* Subtle dot grid */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <svg className="absolute inset-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="hero-dots" width="40" height="40" patternUnits="userSpaceOnUse">
                <circle cx="20" cy="20" r="0.7" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-dots)" />
          </svg>
        </div>

        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 pt-24 pb-16 sm:pt-32 sm:pb-28 lg:pt-36 lg:pb-32">
          <div className="text-center">
            {/* Pill badge */}
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/[0.08] border border-teal-400/[0.12] backdrop-blur-sm mb-8 sm:mb-10 animate-fade-in" data-testid="hero-pill-badge">
              <div className="relative">
                <div className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                <div className="absolute inset-0 w-1.5 h-1.5 rounded-full bg-teal-400 animate-ping" />
              </div>
              <span className="text-[12px] font-semibold text-teal-300/90 tracking-wide">AI-Powered Income Discovery</span>
            </div>

            {/* Headline — static copy */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-[1.15] tracking-tight mb-5 animate-fade-in-up" data-testid="hero-headline">
              Find the best side hustle for<br className="hidden sm:block" />
              <span className="text-teal-400"> your situation — ranked honestly.</span>
            </h1>

            {/* Sub-headline — short, conversion-focused */}
            <p className="text-base sm:text-lg text-slate-300 mb-8 max-w-2xl mx-auto leading-relaxed animate-fade-in-up" style={{ animationDelay: '40ms' }} data-testid="hero-subheadline">
              Compare side hustles, gig apps, and freelance platforms side-by-side. Filtered for your country, schedule, and goals. <span className="text-teal-300 font-medium">No platform pays to rank higher here.</span>
            </p>

            {/* Primary CTA + secondary scroll link */}
            <div className="flex flex-col items-center gap-3 mb-10 sm:mb-12 animate-fade-in-up" style={{ animationDelay: '80ms' }}>
              <Button
                onClick={() => navigate('/match')}
                size="lg"
                className="w-full sm:w-auto bg-teal-500 hover:bg-teal-400 text-slate-900 font-bold px-10 h-14 rounded-full text-base shadow-[0_0_30px_rgba(20,184,166,0.3)] hover:shadow-[0_0_40px_rgba(20,184,166,0.45)] transition-all duration-300"
                data-testid="hero-primary-cta"
              >
                Find My Side Hustle
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <button
                onClick={() => {
                  const el = document.getElementById('country-grid');
                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="text-sm text-slate-400 hover:text-teal-300 transition-colors font-medium"
                data-testid="hero-secondary-scroll"
              >
                Or browse by country ↓
              </button>
            </div>

            {/* THE Search Bar */}
            <div className="animate-fade-in-up relative" style={{ animationDelay: '160ms', isolation: 'isolate' }}>
              <HeroSearchBar />
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TRUST BAR — 4 trust signals ═══ */}
      <section className="py-6 sm:py-8 bg-white border-b border-slate-100" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 sm:gap-y-0">
            {[
              { icon: Globe, label: '46+ Countries Covered', testid: 'trust-bar-countries' },
              { icon: ShieldCheck, label: 'No Paid Rankings — Ever', testid: 'trust-bar-no-paid' },
              { icon: BookOpen, label: 'Editorially Independent', testid: 'trust-bar-editorial' },
              { icon: Landmark, label: 'UK Operated', testid: 'trust-bar-uk' },
            ].map(item => (
              <div key={item.label} className="flex items-center justify-center sm:justify-start gap-2.5" data-testid={item.testid}>
                <item.icon className="h-4 w-4 text-teal-500 flex-shrink-0" />
                <span className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.08em] text-slate-600 whitespace-nowrap">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ "WHY TRUST US" CALLOUT — Reframed editorial independence ═══ */}
      <section className="py-10 sm:py-12 bg-gradient-to-b from-white to-slate-50" data-testid="why-trust-us">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 mb-4">
            <Shield className="h-3.5 w-3.5 text-teal-600" />
            <span className="text-[11px] font-semibold text-teal-700 uppercase tracking-wider">Why trust us</span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-[28px] font-semibold text-slate-900 tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="why-trust-us-heading">
            No platform can pay to rank higher here.
          </h2>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed" data-testid="why-trust-us-body">
            Every comparison is based on real criteria — flexibility, startup cost, earnings potential, and platform reliability. We're independently operated from the UK with no investor pressure to favour anyone.
          </p>
        </div>
      </section>

      {/* ═══ QUESTIONNAIRE CTA ═══ */}
      <section className="py-8 bg-white" data-testid="questionnaire-cta-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <QuestionnairePrompt variant="hero" />
        </div>
      </section>

      {/* ═══ CATEGORIES ═══ */}
      <section className="py-20 lg:py-24 bg-white" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('home.explore')}</span>
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {t('home.browseByCategory')}
              </h2>
              {categoryStats.length > 0 && (
                <p className="text-sm text-slate-400 mt-1">{categoryStats.length} categories &middot; {categoryStats.reduce((s, c) => s + c.count, 0)} opportunities</p>
              )}
            </div>
            <Button onClick={() => navigate('/browse')} variant="ghost" className="hidden sm:flex text-slate-500 hover:text-slate-900 text-sm">
              View all <ChevronRight className="h-4 w-4 ml-0.5" />
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {(categoryStats.length > 0 ? categoryStats : Object.keys(CATEGORY_ICONS).map(name => ({ name, count: 0 }))).map((stat) => (
              <CategoryCard
                key={stat.name}
                name={stat.name}
                icon={CATEGORY_ICONS[stat.name]}
                stat={stat}
                t={t}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ═══ AI-PERSONALIZED MATCHES PREVIEW ═══ */}
      {recommendations.length > 0 && (
        <section className="py-20 lg:py-24 bg-gradient-to-b from-slate-50 to-white" data-testid="ai-matches-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-100 mb-4">
                  <Sparkles className="h-3.5 w-3.5 text-violet-500" />
                  <span className="text-xs font-semibold text-violet-600">AI-Personalized</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Your top matches
                </h2>
                <p className="text-base text-slate-500 mt-2">Based on your skills, assets, and availability</p>
              </div>
              <Button onClick={() => navigate('/dashboard')} variant="ghost" className="hidden sm:flex text-slate-500 hover:text-slate-900">
                See all matches <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {recommendations.map(opp => (
                <OpportunityCard
                  key={opp.opportunity_id}
                  opp={opp}
                  variant={matchScores[opp.opportunity_id] >= 80 ? 'best-match' : 'recommended'}
                  matchScore={matchScores[opp.opportunity_id]}
                  matchReasons={opp.match_reasons}
                  matchConflicts={opp.match_conflicts}
                  matchConfidence={opp.match_confidence}
                  matchCategory={opp.match_category}
                  currency={currency}
                  userCountry={userCountry}
                  isLoggedIn={isAuthenticated}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══ TRENDING ═══ */}
      {trendingOpportunities.length > 0 && (
        <section className="py-20 lg:py-24" data-testid="trending-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="flex items-end justify-between mb-12">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('home.popular')}</span>
                <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('home.trendingOpportunities')}
                </h2>
              </div>
              <Button onClick={() => navigate('/browse')} variant="ghost" className="hidden sm:flex text-slate-500 hover:text-slate-900" data-testid="view-all-button">
                {t('common.viewAll')} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {trendingOpportunities.slice(0, trendingVisible).map(opp => (
                <OpportunityCard
                  key={opp.opportunity_id}
                  opp={opp}
                  variant={matchScores[opp.opportunity_id] >= 60 ? 'recommended' : 'standard'}
                  matchScore={matchScores[opp.opportunity_id]}
                  currency={currency}
                  userCountry={userCountry}
                  isLoggedIn={isAuthenticated}
                />
              ))}
            </div>

            {trendingVisible < trendingOpportunities.length && (
              <div className="text-center mt-12">
                <Button
                  onClick={() => setTrendingVisible(prev => prev + 6)}
                  variant="outline"
                  className="px-8 rounded-full border-slate-300 hover:border-slate-400 text-slate-600 hover:text-slate-900 transition-all"
                  data-testid="load-more-trending-button"
                >
                  {t('home.loadMore')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ═══ GLOBAL AVAILABILITY ═══ */}
      <section className="py-20 lg:py-24 bg-slate-900 relative overflow-hidden" data-testid="global-section">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-teal-500/[0.04] blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-blue-500/[0.03] blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20 mb-6">
                <MapPin className="h-3.5 w-3.5 text-teal-400" />
                <span className="text-xs font-semibold text-teal-300">Location-Aware</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Opportunities wherever
                <br className="hidden sm:block" />
                you are in the world.
              </h2>
              <p className="text-base text-slate-400 mb-8 leading-relaxed max-w-lg">
                Our location intelligence covers 51 countries with region-specific platform recommendations, seasonal earning patterns, local tax awareness, and market insights.
              </p>
              <div className="grid grid-cols-3 gap-6">
                {[
                  { value: '51', label: 'Countries' },
                  { value: '6', label: 'Continents' },
                  { value: '12+', label: 'Currencies' },
                ].map(s => (
                  <div key={s.label}>
                    <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</div>
                    <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { region: 'North America', platforms: 'DoorDash, Instacart, TaskRabbit', icon: '🇺🇸' },
                { region: 'Europe', platforms: 'Deliveroo, Glovo, Wolt', icon: '🇬🇧' },
                { region: 'Asia-Pacific', platforms: 'Grab, Swiggy, Gojek', icon: '🇮🇳' },
                { region: 'Latin America', platforms: 'Rappi, iFood, Workana', icon: '🇧🇷' },
                { region: 'Middle East', platforms: 'Careem, Talabat, Nabbesh', icon: '🇦🇪' },
                { region: 'Africa', platforms: 'Bolt, Glovo, Jumia', icon: '🇳🇬' },
              ].map(r => (
                <div key={r.region} className="bg-white/[0.04] backdrop-blur-sm border border-white/[0.06] rounded-xl p-4">
                  <div className="text-xl mb-2">{r.icon}</div>
                  <h4 className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{r.region}</h4>
                  <p className="text-[11px] text-slate-500 leading-relaxed">{r.platforms}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section id="how-it-works" className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('home.getStarted')}</span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('home.howItWorks')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              {
                num: '01',
                title: 'Tell us about yourself',
                desc: 'Complete a quick profile: your skills, available time, assets you own, and what kind of income you want.',
                icon: Target,
                accent: 'bg-blue-50 text-blue-600 border-blue-100',
              },
              {
                num: '02',
                title: 'Get matched intelligently',
                desc: 'Our AI engine analyzes your profile against all verified opportunities and ranks them by personal fit.',
                icon: Sparkles,
                accent: 'bg-teal-50 text-teal-600 border-teal-100',
              },
              {
                num: '03',
                title: 'Start earning or build your pathway',
                desc: 'Dive into personalized tips, location-aware strategies, and step-by-step career progressions.',
                icon: TrendingUp,
                accent: 'bg-amber-50 text-amber-600 border-amber-100',
              },
            ].map((step, i) => (
              <div key={step.num} className="relative text-center">
                {/* Step number + icon */}
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl border ${step.accent} mb-6`}>
                  <step.icon className="h-7 w-7" />
                </div>
                {/* Connector line */}
                {i < 2 && (
                  <div className="hidden md:block absolute top-8 left-[calc(50%+48px)] w-[calc(100%-96px)] h-px bg-slate-200" />
                )}
                <div className="text-[10px] font-bold text-slate-300 uppercase tracking-[0.2em] mb-2">Step {step.num}</div>
                <h3 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{step.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ CAREER PATHWAYS PREVIEW ═══ */}
      <section className="py-20 lg:py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 mb-6">
                <Route className="h-3.5 w-3.5 text-indigo-500" />
                <span className="text-xs font-semibold text-indigo-600">Career Pathways</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Not just gigs &mdash;
                <br />
                real earning journeys.
              </h2>
              <p className="text-base text-slate-500 mb-8 leading-relaxed">
                Our AI builds multi-step career progressions from where you are now to where you want to be. Every opportunity is a stepping stone.
              </p>
              <div className="space-y-4">
                {[
                  { step: 'Start', label: 'Micro-tasks & surveys to build momentum', color: 'bg-emerald-500' },
                  { step: 'Grow', label: 'Freelancing & gig work to build skills', color: 'bg-blue-500' },
                  { step: 'Scale', label: 'Consulting & passive income for long-term wealth', color: 'bg-violet-500' },
                ].map(p => (
                  <div key={p.step} className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl ${p.color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                      {p.step.charAt(0)}
                    </div>
                    <div>
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{p.step}</span>
                      <p className="text-sm text-slate-600">{p.label}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => navigate('/dashboard')}
                className="mt-8 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6 transition-all duration-200"
              >
                Explore Pathways
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            <div className="hidden lg:block">
              {/* Visual progression illustration */}
              <div className="relative">
                <div className="space-y-4">
                  {['Beginner Gig Worker', 'Skilled Freelancer', 'Niche Expert', 'Portfolio Income'].map((stage, i) => (
                    <div key={stage} className="flex items-center gap-4" style={{ paddingLeft: `${i * 32}px` }}>
                      <div className={`w-3 h-3 rounded-full ${i === 3 ? 'bg-teal-500' : 'bg-slate-300'} ring-4 ${i === 3 ? 'ring-teal-100' : 'ring-slate-100'}`} />
                      <div className={`flex-1 py-4 px-5 rounded-xl border ${i === 3 ? 'bg-teal-50 border-teal-200 text-teal-700' : 'bg-white border-slate-200 text-slate-600'}`}>
                        <span className={`text-sm font-medium ${i === 3 ? 'text-teal-700' : 'text-slate-700'}`}>{stage}</span>
                        <span className="text-xs text-slate-400 block mt-0.5">
                          {['Casual income', 'Steady side income', 'Scalable income', 'Long-term potential'][i]}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ EDITORIAL STANDARDS BAR ═══ */}
      <section className="bg-white border-t border-slate-100" data-testid="editorial-standards-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12 lg:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-start">
            {/* Left: intro */}
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 mb-4">
                <BookOpen className="h-3.5 w-3.5 text-slate-500" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Editorial standards</span>
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Built on clear editorial standards
              </h2>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed">
                HustleHub AI ranks and explains opportunities using transparent criteria:
                earning realism, availability, safety signals, user fit, and platform
                transparency. Affiliate relationships do not guarantee placement or
                positive scoring.
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2">
                <Link
                  to="/editorial-policy"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-teal-600 transition-colors"
                  data-testid="editorial-standards-policy-link"
                >
                  Read our Editorial Policy <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/how-we-rank"
                  className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors"
                  data-testid="editorial-standards-how-we-rank-link"
                >
                  How we rank opportunities
                </Link>
              </div>
            </div>

            {/* Right: 4 mini-points */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="editorial-standards-pillars">
                {[
                  { icon: Shield, label: 'Independent scoring', desc: 'Rankings are not influenced by commercial relationships.' },
                  { icon: DollarSign, label: 'Realistic earnings guidance', desc: 'We show ranges and caveats — not headline promises.' },
                  { icon: CheckCircle, label: 'Trust & safety checks', desc: 'We screen for transparency, payout clarity and red flags.' },
                  { icon: Eye, label: 'Affiliate disclosure', desc: 'Outbound links that may earn us commission are disclosed.' },
                ].map(p => (
                  <div
                    key={p.label}
                    className="flex items-start gap-3 p-3.5 rounded-xl border border-slate-200/70 bg-slate-50/50"
                    data-testid={`editorial-pillar-${p.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                      <p.icon className="h-3.5 w-3.5 text-slate-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[13px] font-semibold text-slate-900 leading-tight">{p.label}</p>
                      <p className="text-[12px] text-slate-500 mt-0.5 leading-relaxed">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ TRUST & SAFETY PROMISE ═══ */}
      <section className="py-20 lg:py-24 bg-white" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built on trust & safety
            </h2>
            <p className="text-base text-slate-500 mt-3 max-w-2xl mx-auto">
              Every opportunity on Hustle-HubAI is reviewed using our editorial trust-and-safety criteria — covering transparency, earning realism, availability, user fit, and safety signals.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: 'AI Safety Scoring',
                desc: 'Every listing is evaluated across 8+ risk factors including legitimacy, scam potential, and barrier of entry.',
                accent: 'from-teal-500 to-emerald-600',
              },
              {
                icon: Lock,
                title: 'Ethics-Audited Tips',
                desc: 'All earning tips pass through our Ethics Audit Engine. No misleading claims, no platform manipulation.',
                accent: 'from-blue-500 to-indigo-600',
              },
              {
                icon: CheckCircle,
                title: 'Community Moderation',
                desc: 'User reports are classified by AI and escalated appropriately. Bad actors get removed fast.',
                accent: 'from-violet-500 to-purple-600',
              },
            ].map(card => (
              <div key={card.title} className="bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:shadow-[0_4px_16px_rgba(15,23,42,0.06)] transition-all duration-300">
                <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${card.accent} mb-5`}>
                  <card.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{card.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SEO DISCOVERY HUB — Internal links for crawlers + users ═══ */}
      <section id="country-grid" className="py-16 lg:py-20 bg-slate-50 border-t border-slate-100" data-testid="seo-hub-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-8 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Explore Side Hustles Worldwide
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Countries */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 text-teal-500" />
                By Country
              </h3>
              <ul className="space-y-1.5">
                {[
                  { to: '/uk', label: 'United Kingdom' },
                  { to: '/us', label: 'United States' },
                  { to: '/canada', label: 'Canada' },
                  { to: '/australia', label: 'Australia' },
                  { to: '/germany', label: 'Germany' },
                  { to: '/france', label: 'France' },
                  { to: '/spain', label: 'Spain' },
                  { to: '/netherlands', label: 'Netherlands' },
                  { to: '/italy', label: 'Italy' },
                  { to: '/ireland', label: 'Ireland' },
                  { to: '/portugal', label: 'Portugal' },
                  { to: '/sweden', label: 'Sweden' },
                  { to: '/poland', label: 'Poland' },
                  { to: '/switzerland', label: 'Switzerland' },
                  { to: '/belgium', label: 'Belgium' },
                ].map(c => (
                  <li key={c.to}><a href={c.to} className="text-sm text-slate-500 hover:text-teal-600 transition-colors">{c.label}</a></li>
                ))}
              </ul>
            </div>

            {/* Cities */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-teal-500" />
                Popular Cities
              </h3>
              <ul className="space-y-1.5">
                {[
                  { to: '/side-hustles-london', label: 'London' },
                  { to: '/side-hustles-manchester', label: 'Manchester' },
                  { to: '/side-hustles-new-york', label: 'New York' },
                  { to: '/side-hustles-toronto', label: 'Toronto' },
                  { to: '/side-hustles-sydney', label: 'Sydney' },
                  { to: '/side-hustles-berlin', label: 'Berlin' },
                  { to: '/side-hustles-paris', label: 'Paris' },
                  { to: '/side-hustles-amsterdam', label: 'Amsterdam' },
                  { to: '/side-hustles-rome', label: 'Rome' },
                  { to: '/side-hustles-dublin', label: 'Dublin' },
                  { to: '/side-hustles-lisbon', label: 'Lisbon' },
                  { to: '/side-hustles-stockholm', label: 'Stockholm' },
                  { to: '/side-hustles-warsaw', label: 'Warsaw' },
                  { to: '/side-hustles-prague', label: 'Prague' },
                  { to: '/side-hustles-athens', label: 'Athens' },
                ].map(c => (
                  <li key={c.to}><a href={c.to} className="text-sm text-slate-500 hover:text-teal-600 transition-colors">{c.label}</a></li>
                ))}
              </ul>
            </div>

            {/* Audience & Resources */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-teal-500" />
                For You
              </h3>
              <ul className="space-y-1.5">
                {[
                  { to: '/side-hustles-for-students', label: 'Students' },
                  { to: '/side-hustles-for-retirees', label: 'Retirees & Over 50s' },
                  { to: '/nhs-opportunities', label: 'NHS Workers' },
                  { to: '/ways-to-earn/extra-money-fast', label: 'Earn Extra Money Fast' },
                  { to: '/launch-your-hustle', label: 'Launch Your Hustle' },
                  { to: '/match', label: 'AI Side Hustle Matching' },
                  { to: '/profile-builder', label: 'Build Your Profile' },
                ].map(c => (
                  <li key={c.to}><a href={c.to} className="text-sm text-slate-500 hover:text-teal-600 transition-colors">{c.label}</a></li>
                ))}
              </ul>

              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 mt-6 flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5 text-teal-500" />
                Compare & Discover
              </h3>
              <ul className="space-y-1.5">
                {[
                  { to: '/browse', label: 'Browse All' },
                  { to: '/trending', label: 'Trending Now' },
                  { to: '/top-rated', label: 'Top Rated' },
                  { to: '/compare', label: 'Compare Side Hustles' },
                  { to: '/new', label: 'Newly Added' },
                ].map(c => (
                  <li key={c.to}><a href={c.to} className="text-sm text-slate-500 hover:text-teal-600 transition-colors">{c.label}</a></li>
                ))}
              </ul>
            </div>

            {/* Localized Pages */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 text-teal-500" />
                In Your Language
              </h3>
              <ul className="space-y-1.5">
                {[
                  { to: '/de/nebenjobs-deutschland', label: 'Nebenjobs — Deutschland' },
                  { to: '/fr/petits-boulots-france', label: 'Petits Boulots — France' },
                  { to: '/es/trabajos-extra-espana', label: 'Trabajos Extra — España' },
                  { to: '/nl/bijbanen-nederland', label: 'Bijbanen — Nederland' },
                  { to: '/pt/trabalhos-extra-portugal', label: 'Trabalhos Extra — Portugal' },
                  { to: '/it/lavoretti-italia', label: 'Lavoretti — Italia' },
                  { to: '/sv/extrajobb-sverige', label: 'Extrajobb — Sverige' },
                  { to: '/pl/dodatkowa-praca-polska', label: 'Dodatkowa Praca — Polska' },
                  { to: '/da/bijob-danmark', label: 'Bijob — Danmark' },
                  { to: '/no/bijobb-norge', label: 'Bijobb — Norge' },
                  { to: '/fi/sivutyo-suomi', label: 'Sivutyöt — Suomi' },
                  { to: '/cs/brigady-cesko', label: 'Brigády — Česko' },
                  { to: '/el/extras-douleies-ellada', label: 'Extras Douleies — Ελλάδα' },
                ].map(c => (
                  <li key={c.to}><a href={c.to} className="text-sm text-slate-500 hover:text-teal-600 transition-colors">{c.label}</a></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ AI MATCH CALLOUT — Primary conversion section ═══ */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white" data-testid="ai-match-callout">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-50 border border-violet-100 mb-5">
            <Sparkles className="h-3.5 w-3.5 text-violet-500" />
            <span className="text-[11px] font-semibold uppercase tracking-wider text-violet-700">AI Match</span>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="ai-match-heading">
            Not sure where to start?
          </h2>
          <p className="text-base sm:text-lg text-slate-500 leading-relaxed mb-8 max-w-2xl mx-auto" data-testid="ai-match-sub">
            Answer a few quick questions. Get a personalised shortlist of opportunities matched to your country, schedule, and earning goals.
          </p>
          <Button
            onClick={() => navigate('/match')}
            size="lg"
            className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-bold px-10 h-14 rounded-full text-base shadow-[0_4px_20px_rgba(15,23,42,0.15)] hover:shadow-[0_6px_28px_rgba(15,23,42,0.22)] transition-all duration-300"
            data-testid="ai-match-cta-button"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            Start AI Matching
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* ═══ AUDIENCE SEGMENT CARDS — Visual cards for key audiences ═══ */}
      <section className="py-16 sm:py-20 lg:py-24 bg-slate-50 border-t border-slate-100" data-testid="audience-cards-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-10 sm:mb-12">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400" style={{ fontFamily: 'Outfit, sans-serif' }}>For Your Situation</span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight mt-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Side hustles by audience
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {[
              {
                to: '/nhs-opportunities',
                icon: Stethoscope,
                accent: 'bg-rose-50 text-rose-600 border-rose-100',
                label: 'NHS & Healthcare Workers',
                benefit: 'Shift-compatible opportunities that use your clinical skills.',
                testid: 'audience-card-nhs',
              },
              {
                to: '/side-hustles-for-students',
                icon: GraduationCap,
                accent: 'bg-blue-50 text-blue-600 border-blue-100',
                label: 'Students',
                benefit: 'Flexible income around lectures — tutoring, surveys, gig work.',
                testid: 'audience-card-students',
              },
              {
                to: '/side-hustles-for-retirees',
                icon: Heart,
                accent: 'bg-amber-50 text-amber-600 border-amber-100',
                label: 'Retirees & Over 50s',
                benefit: 'Low-pressure earning options that fit a relaxed schedule.',
                testid: 'audience-card-retirees',
              },
              {
                to: '/ways-to-earn/extra-money-fast',
                icon: Zap,
                accent: 'bg-emerald-50 text-emerald-600 border-emerald-100',
                label: 'Earn Extra Money Fast',
                benefit: 'Shorter time-to-start options when you need cash this week.',
                testid: 'audience-card-extra-fast',
              },
              {
                to: '/launch-your-hustle',
                icon: Route,
                accent: 'bg-indigo-50 text-indigo-600 border-indigo-100',
                label: 'Build Your Own Hustle',
                benefit: 'Step-by-step paths from first gig to scalable income.',
                testid: 'audience-card-launch',
              },
              {
                to: '/match',
                icon: Sparkles,
                accent: 'bg-violet-50 text-violet-600 border-violet-100',
                label: 'Not sure yet?',
                benefit: 'Let our AI match you to the best opportunities in 5 questions.',
                testid: 'audience-card-ai-match',
              },
            ].map(card => (
              <Link
                key={card.to}
                to={card.to}
                data-testid={card.testid}
                className="group bg-white rounded-2xl p-5 sm:p-6 border border-slate-200/80 hover:border-slate-300 hover:shadow-[0_8px_28px_rgba(15,23,42,0.07)] hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className={`inline-flex p-2.5 rounded-xl border ${card.accent} mb-4`}>
                  <card.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base sm:text-[17px] font-semibold text-slate-900 mb-1.5 group-hover:text-teal-600 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {card.label}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed mb-3">
                  {card.benefit}
                </p>
                <span className="inline-flex items-center gap-1 text-sm font-semibold text-teal-600 group-hover:gap-1.5 transition-all">
                  Browse <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF — Pre-footer statement ═══ */}
      <section className="py-12 sm:py-14 bg-white border-t border-slate-100" data-testid="social-proof-section">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <p className="text-base sm:text-lg text-slate-600 leading-relaxed" data-testid="social-proof-text">
            Join thousands of people across <span className="font-semibold text-slate-900">46 countries</span> finding better ways to earn — no spam, no paid placements, no get-rich-quick noise.
          </p>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-24 lg:py-32 bg-slate-900 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 right-0 w-[500px] h-[500px] rounded-full bg-emerald-500/[0.05] blur-3xl" />
          <div className="absolute -bottom-40 left-0 w-[400px] h-[400px] rounded-full bg-blue-500/[0.04] blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your next income stream
            <br className="hidden sm:block" />
            is waiting.
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto leading-relaxed">
            Join the community finding smarter, safer ways to earn. Matched by AI. Reviewed for trust. Available worldwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/profile-builder')}
              size="lg"
              className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-bold px-10 h-13 rounded-full text-base shadow-[0_0_30px_rgba(20,184,166,0.25)] hover:shadow-[0_0_40px_rgba(20,184,166,0.35)] transition-all duration-300"
              data-testid="cta-button"
            >
              <Sparkles className="mr-2 h-4 w-4" />
              Start with AI Matching
            </Button>
            <Button
              onClick={() => navigate('/browse')}
              size="lg"
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-white/[0.06] hover:text-white h-13 rounded-full px-10 text-base transition-all duration-300"
            >
              Browse All Opportunities
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default HomePage;
